import React, { useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import i18n from 'simple-react-i18n'
import { useDispatch, useSelector } from 'react-redux'
import { Card, CardContent, Grid } from '@mui/material'
import { chunk, isNil, keyBy, sortBy, uniq } from 'lodash'
import { CardTitle } from '../../../components/card/NewCard'
import ProgressCard from '../../../components/card/ProgressCard'
import RpqsAction from '../../actions/RpqsAction'
import DistributionUnitAction from '../../../distributionUnit/actions/DistributionUnitAction'
import DistributionUnitThunk from '../../../distributionUnit/actions/DistributionUnitThunk'
import { DistributionUnitConformityAccordion } from '../../../distributionUnit/components/DistributionUnitsConformity'
import ContributorAction from '../../../referencial/components/contributor/actions/ContributorAction'
import { DEFAULT_SUPPORT, SEUILS_LIM, SEUILS_REF } from '../../../quality/components/AEPoverview/AEPOverview'
import { getConformityFilterBody } from '../../../quality/utils/conformityExportUtils'
import { STATION_TYPE_CONSTANT } from '../../../station/constants/StationConstants'
import useApplicationSetting from '../../../utils/customHook/useApplicationSetting'
import useAccountSetting from '../../../utils/customHook/useAccountSetting'
import useLocalStorage from '../../../utils/customHook/useLocalStorage'
import { hasValue } from '../../../utils/NumberUtil'

// mêmes réglages de seuils que l'écran Bilan conformité des UDI
const STORAGE_SEUILS_REF = 'DEFAULT_REFERENCE_THRESHOLD'
const STORAGE_SEUILS_LIM = 'DEFAULT_LIMIT_THRESHOLD'
const GROUP_SIZE = 20

const toInt = setting => setting ? parseInt(setting) : undefined

const getDistributionUnitCodes = managementUnits => uniq(managementUnits.flatMap(managementUnit => (managementUnit?.link_stations ?? [])
    .filter(link => link.stationType === STATION_TYPE_CONSTANT.DISTRIBUTION)
    .map(link => link.stationCode)))

const RpqsConformityPanel = ({ managementEntities = [], year }) => {
    const dispatch = useDispatch()
    const distributionUnits = useSelector(store => store.DistributionUnitReducer.distributionUnits)

    const defaultSupport = useAccountSetting(DEFAULT_SUPPORT, setting => setting || undefined)
    const thresholdRefSettings = useApplicationSetting(SEUILS_REF, toInt)
    const thresholdRefAccountSettings = useAccountSetting(SEUILS_REF, toInt)
    const thresholdRefStorage = toInt(useLocalStorage(STORAGE_SEUILS_REF)[0])
    const thresholdLimSettings = useApplicationSetting(SEUILS_LIM, toInt)
    const thresholdLimAccountSettings = useAccountSetting(SEUILS_LIM, toInt)
    const thresholdLimStorage = toInt(useLocalStorage(STORAGE_SEUILS_LIM)[0])

    const [distributionUnitCodes, setDistributionUnitCodes] = useState()
    const [results, setResults] = useState({})
    const [expandedUnits, setExpandedUnits] = useState({})

    useEffect(() => {
        dispatch(ContributorAction.fetchProducers())
        if (!distributionUnits.length) {
            dispatch(DistributionUnitThunk.fetchDistributionUnits())
        }
    }, [dispatch])

    // la liste des entités de gestion ne porte pas leurs liens : chaque entité est relue pour ses UDI liées
    const managementEntityIdsKey = managementEntities.map(({ id }) => id).join(',')
    useEffect(() => {
        setDistributionUnitCodes(undefined)
        Promise.all(managementEntities.map(({ id }) => RpqsAction.promiseManagementUnitById(id).catch(() => undefined)))
            .then(managementUnits => setDistributionUnitCodes(getDistributionUnitCodes(managementUnits)))
    }, [managementEntityIdsKey])

    const units = useMemo(() => sortBy(
        distributionUnits.filter(udi => distributionUnitCodes?.includes(udi.code)),
        udi => udi.name?.toUpperCase(),
    ), [distributionUnits, distributionUnitCodes])

    const filter = useMemo(() => ({
        year: parseInt(year),
        support: defaultSupport,
        referenceThreshold: thresholdRefStorage ?? thresholdRefAccountSettings ?? thresholdRefSettings,
        limitThreshold: thresholdLimStorage ?? thresholdLimAccountSettings ?? thresholdLimSettings,
    }), [year, defaultSupport, thresholdRefStorage, thresholdRefAccountSettings, thresholdRefSettings, thresholdLimStorage, thresholdLimAccountSettings, thresholdLimSettings])

    const hasThreshold = hasValue(filter.referenceThreshold) || hasValue(filter.limitThreshold)
    const unitIdsKey = units.map(({ id }) => id).join(',')
    const filterKey = JSON.stringify(filter)

    useEffect(() => {
        if (!unitIdsKey || !hasThreshold) {
            return undefined
        }
        const controller = new AbortController()
        const body = getConformityFilterBody(filter)
        const ids = units.map(({ id }) => id)
        setResults({})
        chunk(ids, GROUP_SIZE).forEach(groupIds => DistributionUnitAction.promiseConformity(groupIds, body, controller.signal)
            .then(groupResults => {
                const resultsById = keyBy(Array.isArray(groupResults) ? groupResults : [], 'id')
                setResults(prev => ({ ...prev, ...Object.fromEntries(groupIds.map(id => [id, resultsById[id] || { error: true }])) }))
            })
            .catch(() => !controller.signal.aborted && setResults(prev => ({ ...prev, ...Object.fromEntries(groupIds.map(id => [id, { error: true }])) }))))
        return () => controller.abort()
    }, [unitIdsKey, filterKey, hasThreshold])

    const getContent = () => {
        if (isNil(distributionUnitCodes)) {
            return <ProgressCard indeterminate />
        }
        if (!hasThreshold) {
            return <Grid>{i18n.noThresholdSelectedError}</Grid>
        }
        if (!units.length) {
            return <Grid>{i18n.noResults}</Grid>
        }
        return units.map(udi => (
            <DistributionUnitConformityAccordion
                key={udi.id}
                udi={udi}
                result={results[udi.id]}
                loading={isNil(results[udi.id])}
                isSearchDone
                filter={filter}
                expanded={expandedUnits[udi.id] ?? true}
                onToggle={(unitId, isExpanded) => setExpandedUnits(prev => ({ ...prev, [unitId]: isExpanded }))}
            />
        ))
    }

    return (
        <Card>
            <CardTitle title={`${i18n.AEPoverview} (${units.length} ${units.length > 1 ? i18n.elements : i18n.element})`} />
            <CardContent>
                {getContent()}
            </CardContent>
        </Card>
    )
}

RpqsConformityPanel.propTypes = {
    managementEntities: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.number,
    })),
    year: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
}

export default RpqsConformityPanel
